import React, { useEffect, useState } from 'react';
import { useIsFetching, useQueryClient } from 'react-query';
import styled from 'styled-components/macro';
import Header from './Header';
import Departments from './widgets/Departments';
import VideoDelivery from './widgets/VideoDelivery';
import PieChartDesc from './widgets/PieChartDesc';
import VideoEngagements from './widgets/VideoEngagements';
import TopUsersComparison from './widgets/TopUsersComparison';
import { widget } from './widgets/keys';
import dayjs from 'dayjs';
import {
  getCommonWidgets,
  getAutomotiveWidgets,
  WidgetValues,
  WidgetLabels,
  Field,
  REPORTS_STALE_TIME,
  getDefaultWidgets,
} from './widgets/types';
import Masonry from 'react-masonry-css';
import { theme } from 'lib/style';
import VideoPieChart from './widgets/VideoPieChart';
import { useAuth } from 'lib/context';
import DeliveryPieChart from './widgets/DeliveryPieChart';
import ViewRateByDelivery from './widgets/ViewRateByDelivery';
import TopCtaLinks from './widgets/TopCtaLinks';
import TopCtaSets from './widgets/TopCtaSets';
import TopUsers from './widgets/TopUsers';
import Leaderboard from './widgets/Leaderboard';
import TopVideos from './widgets/TopVideos';
import { differenceBy } from 'lodash';
import {
  calculateDatesForRange,
  getDateRangeFromLocalstorage,
} from '../../components/DateRangeSelector/DateRangeSelector';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { LastFetched } from 'lib/components/LastFetched';
import { timeRangeConstants } from '../../components/DateRangeSelector/useTimeRangeOptions';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { ButtonWithTableFieldsModal } from 'lib/components/buttonWithTableFieldsModal';

const DATE_FORMAT = 'YYYY-MM-DD';

const WidgetContainer = styled.div`
  max-width: 1008px;
  margin: 0 auto;
  @media screen and (min-width: 1280px) {
    max-width: 1600px;
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  > button {
    margin: 0;
    > .text {
      color: ${theme.palette.black_1_100};
    }
  }
`;

const StickyContainter = styled.div`
  height: 200px;
`;

const AUTOMOTIVE_LOCALSTORAGE_FIELDS = 'Automotive';
const COVIDEO_LOCALSTORAGE_FIELDS = 'General';

const Dashboard = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const { isAutomotive } = userData;

  const location = useLocation();
  const queryParams: any = queryString.parse(location.search);
  const storedDateRange = getDateRangeFromLocalstorage();
  const initRange =
    queryParams.range ||
    storedDateRange?.range ||
    timeRangeConstants.LAST_7_DAYS;
  const { start, end } = calculateDatesForRange(
    initRange,
    storedDateRange?.start,
    storedDateRange?.end
  );

  const queryClient = useQueryClient();
  const [lastFetched, setLastFetched] = useState<Record<string, Date>>({});

  const [timestamp, setTimeStamp] = useState({
    current: { start, end, range: initRange },
  });

  //  Stable storageKey
  const storageKey = React.useMemo(
    () =>
      isAutomotive
        ? AUTOMOTIVE_LOCALSTORAGE_FIELDS
        : COVIDEO_LOCALSTORAGE_FIELDS,
    [isAutomotive]
  );

  //  function to get all fields (stable across renders unless deps change)
  const getAllFields = React.useCallback(
    (tr: TFunction) =>
      isAutomotive ? getAutomotiveWidgets(tr) : getCommonWidgets(tr),
    [isAutomotive]
  );

  // the array only when needed (isAutomotive or locale changes)
  const allFields = React.useMemo(() => getAllFields(t), [getAllFields, t]);

  //  default widgets; only recompute when the locale changes
  const defaultFields = React.useMemo(() => getDefaultWidgets(t), [t]);

  const [widgets, setWidgets] = useState<Field[]>([]);

  const params = {
    from: dayjs(timestamp?.current?.start).format(DATE_FORMAT),
    to: dayjs(timestamp?.current?.end).format(DATE_FORMAT),
  };

  useEffect(() => {
    if (!lastFetched[params.from]) {
      return setLastFetched(prevState => {
        return { ...prevState, [params.from]: new Date() };
      });
    }
    const currentDate = dayjs(new Date());

    let staledData: number[] = [];

    //check if one of active widget queries is stale
    widgets.forEach(item => {
      const widgetUpdated = queryClient.getQueryState(
        widget.detail(item.value, { ...params })
      )?.dataUpdatedAt;
      const widgetDate = dayjs(widgetUpdated);

      if (
        widgetUpdated &&
        currentDate.diff(widgetDate, 'seconds') > REPORTS_STALE_TIME
      ) {
        queryClient.refetchQueries(widget.detail(item.value, { ...params }), {
          active: true,
        });
        staledData.push(widgetUpdated);
      }

      if (!!staledData.length) {
        return setLastFetched(prevState => {
          return { ...prevState, [params.from]: new Date() };
        });
      }
    });
  }, [params.from]);

  const refetchWidgets = async () => {
    queryClient.refetchQueries(widget.details(), { active: true });
    setLastFetched(prevState => {
      return { ...prevState, [params.from]: new Date() };
    });
  };
  const isWidgetDetailsFetching = useIsFetching({
    queryKey: widget.details(),
  });

  const widgetContainerRef = React.useRef(null);
  const [dataForExport, setDataForExport] = React.useState<any>([]);

  const addDataForExport = (data: any) => {
    setDataForExport((prevValues: any) => {
      const newData = prevValues.filter(
        (item: any) => item.title !== data.title
      );
      return [...newData, data];
    });
  };

  const onModalSave = (fields: Field[]) => {
    const removedWidgets = differenceBy(widgets, fields, 'value');

    removedWidgets.forEach(widgetItem => {
      queryClient.removeQueries(widget.detail(widgetItem.value));
    });
    setWidgets(fields);
  };

  const onSaveCustomTableFields = (fields: Field[]) => {
    onModalSave(fields);
  };

  const translatedWidgets = widgets.map(item => ({
    value: item.value,
    label: t(item.label),
  }));

  return (
    <>
      <StickyContainter>
        <Header
          timeStamp={timestamp}
          setTimeStamp={setTimeStamp}
          dataForExport={dataForExport}
          widgets={widgets}
        />
      </StickyContainter>
      <WidgetContainer>
        <ActionsWrapper>
          <LastFetched
            dataUpdatedAt={dayjs(lastFetched[params.from]).valueOf()}
            isRefetching={!!isWidgetDetailsFetching}
            refetch={refetchWidgets}
          />
          <ButtonWithTableFieldsModal
            allFields={allFields}
            defaultReports={defaultFields}
            setReports={setWidgets}
            onUpdateClick={onSaveCustomTableFields}
            storageKey={storageKey}
            reports={translatedWidgets}
          />
        </ActionsWrapper>

        <Masonry
          className='my-masonry-grid'
          columnClassName='my-masonry-grid_column'
          breakpointCols={{ default: 3, 1600: 2 }}
          ref={widgetContainerRef}
        >
          {widgets.map(({ value }) => {
            switch (value) {
              case WidgetValues.VIDEO_DELIVERY:
                return (
                  <VideoDelivery
                    key={value}
                    params={params}
                    addDataForExport={(val: any) => {
                      addDataForExport(val);
                    }}
                  />
                );
              case WidgetValues.DEPARTMENTS:
                return (
                  <Departments
                    key={value}
                    params={params}
                    addDataForExport={(val: any) => {
                      addDataForExport(val);
                    }}
                  />
                );
              case WidgetValues.VIDEO_ENGAGEMENTS:
                return (
                  <VideoEngagements
                    key={value}
                    params={params}
                    addDataForExport={(val: any) => {
                      addDataForExport(val);
                    }}
                  />
                );
              case WidgetValues.NEW_VIDEOS:
                return (
                  <VideoPieChart
                    key={value}
                    title={WidgetLabels.NEW_VIDEOS}
                    params={params}
                    addDataForExport={addDataForExport}
                  />
                );
              case WidgetValues.DELIVERY_METHOD:
                return (
                  <DeliveryPieChart
                    key={value}
                    title={WidgetLabels.DELIVERY_METHOD}
                    params={params}
                    addDataForExport={addDataForExport}
                  />
                );
              case WidgetValues.VIEW_RATE_BY_DELIVERY_METHOD:
                return (
                  <ViewRateByDelivery
                    key={value}
                    params={params}
                    addDataForExport={addDataForExport}
                  />
                );
              case WidgetValues.TOP_CTA_SETS:
                return (
                  <TopCtaSets
                    key={value}
                    params={params}
                    variant='cta-sets'
                    addDataForExport={(val: any) => addDataForExport(val)}
                  />
                );
              case WidgetValues.TOP_CTA_LINKS:
                return (
                  <TopCtaLinks
                    key={value}
                    params={params}
                    variant='cta-links'
                    addDataForExport={(val: any) => addDataForExport(val)}
                  />
                );
              case WidgetValues.TOP_USERS:
                return (
                  <TopUsers
                    key={value}
                    params={params}
                    variant='users'
                    addDataForExport={(val: any) => addDataForExport(val)}
                  />
                );
              case WidgetValues.TOP_FIVE_USER_COMPARISON:
                return (
                  <TopUsersComparison
                    key={value}
                    params={params}
                    addDataForExport={addDataForExport}
                  />
                );
              case WidgetValues.TOP_VIDEOS:
                return (
                  <TopVideos
                    key={value}
                    params={params}
                    variant='videos'
                    addDataForExport={(val: any) => addDataForExport(val)}
                  />
                );
              case WidgetValues.VIEWS_BY_VIDEO_TYPE: {
                if (isAutomotive) {
                  return (
                    <PieChartDesc
                      key={value}
                      title={WidgetLabels.VIEWS_BY_VIDEO_TYPE}
                      params={params}
                      addDataForExport={addDataForExport}
                    />
                  );
                }
                return null;
              }
              case WidgetValues.LEADERBOARD:
                if (isAutomotive) {
                  return (
                    <Leaderboard
                      key={value}
                      params={params}
                      variant='users'
                      addDataForExport={(val: any) => addDataForExport(val)}
                    />
                  );
                }
                return null;

              default:
                throw Error('Something went wrong');
            }
          })}
        </Masonry>
      </WidgetContainer>
    </>
  );
};
export default Dashboard;
